/**
 * CR-8667 — the Rating filter's request params for the customer-side table.
 *
 * The filter used to run here, over the 20 rows already on screen, so with a
 * sort (or across pages) each page showed only its own few matches while the
 * count still described every supplier. The server now applies it to every
 * supplier before sorting and paging (suppliers/views/performance_rating_filter.py).
 *
 * The server grades with the bands sent here — the same `useRatingBands`
 * thresholds, in the same order, with the same label `getGradeInfo` gives each
 * badge — through a port of `getGradeInfo`, so a row is kept exactly when its
 * badge matches the filter.
 */
import { SupplierRatingThreshold } from '../../lib/api';

/**
 * Params for the Rating filter, or none when it is off.
 *
 * @param rating - the filter value: 'all', 'N/A', or a grade
 * @param thresholds - the bands the Rating column grades with
 * @returns `rating` and, for a grade, the `rating_bands` to grade with
 */
export function ratingFilterParams(
  rating: string,
  thresholds: SupplierRatingThreshold[],
): Record<string, string> {
  if (!rating || rating === 'all') return {};
  if (rating === 'N/A') return { rating };
  const bands = thresholds.map((t) => ({
    // parseFloat, as getGradeInfo reads them (an unreadable edge is NaN → null,
    // which matches nothing on either side).
    min: parseFloat(t.min_score),
    max: parseFloat(t.max_score),
    grade: t.grade,
    label: t.display_label || t.label || t.grade,
  }));
  return { rating, rating_bands: JSON.stringify(bands) };
}
